"use client"

import { useLocale, useTranslations } from "next-intl"
import { useTransition } from "react"

import { useStoreConfig } from "@/components/store-config-provider"
import { usePathname, useRouter } from "@/i18n/navigation"
import { cn } from "@workspace/ui/lib/utils"

/**
 * Per-tenant language switcher (US-014). Lists ONLY the locales the tenant offers
 * (`config.locales`) and swaps the active `[locale]` segment for the SAME page via
 * next-intl navigation (`usePathname` gives the unprefixed path; `router.replace`
 * re-adds the chosen locale prefix). Hidden entirely when the tenant offers a
 * single locale — there is nothing to switch between.
 *
 * Direction-agnostic styling (logical properties only) so it renders correctly in
 * both LTR and RTL tenants.
 */

// Autonyms — each language labelled in its own script so a diner recognises it
// regardless of the current UI language.
const LOCALE_LABELS: Record<string, string> = {
  "en-GB": "English",
  en: "English",
  fa: "فارسی",
  tr: "Türkçe",
}

export function LanguageSwitcher() {
  const { config } = useStoreConfig()
  const activeLocale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const t = useTranslations("languageSwitcher")
  const [isPending, startTransition] = useTransition()

  const locales = config.locales ?? []

  // Nothing to switch between → don't render the control at all.
  if (locales.length <= 1) return null

  function switchTo(locale: string) {
    if (locale === activeLocale) return
    startTransition(() => {
      router.replace(pathname, { locale })
    })
  }

  return (
    <nav aria-label={t("label")} className="flex items-center gap-1">
      {locales.map((locale) => {
        const isActive = locale === activeLocale
        return (
          <button
            key={locale}
            type="button"
            lang={locale}
            aria-current={isActive ? "true" : undefined}
            disabled={isPending}
            onClick={() => switchTo(locale)}
            className={cn(
              "rounded-md px-2 py-1 text-sm font-medium transition-colors",
              "disabled:pointer-events-none disabled:opacity-50",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {LOCALE_LABELS[locale] ?? locale.toUpperCase()}
          </button>
        )
      })}
    </nav>
  )
}
